/**
 * Authentication and Authorization Types
 * Defines user roles, permissions, and access control structures
 */

// ========== User Roles ==========

export type UserRole =
  | 'super_admin'
  | 'platform_admin'
  | 'claims_adjuster'
  | 'verifier'
  | 'insurer_admin'
  | 'insurer_user'
  | 'partner_admin'
  | 'partner_user'
  | 'salvage_admin'
  | 'salvage_user'
  | 'field_agent';

export const USER_ROLES: Record<UserRole, { label: string; description: string }> = {
  super_admin: {
    label: 'Super Admin',
    description: 'Full system access across all tenants',
  },
  platform_admin: {
    label: 'Platform Admin',
    description: 'Tenant administration and platform management',
  },
  claims_adjuster: {
    label: 'Claims Adjuster',
    description: 'Process and adjudicate insurance claims',
  },
  verifier: {
    label: 'Verifier',
    description: 'Verify and validate inspection reports',
  },
  insurer_admin: {
    label: 'Insurer Admin',
    description: 'Insurance company administrator',
  },
  insurer_user: {
    label: 'Insurer User',
    description: 'Insurance company user',
  },
  partner_admin: {
    label: 'Partner Admin',
    description: 'Partner organization administrator',
  },
  partner_user: {
    label: 'Partner User',
    description: 'Partner organization user',
  },
  salvage_admin: {
    label: 'Salvage Admin',
    description: 'Salvage yard administrator',
  },
  salvage_user: {
    label: 'Salvage User',
    description: 'Salvage yard user',
  },
  field_agent: {
    label: 'Field Agent',
    description: 'On-site inspection agent',
  },
};

// ========== Permissions ==========

export type Permission =
  // User Management
  | 'users.view'
  | 'users.create'
  | 'users.edit'
  | 'users.delete'
  | 'users.assign_roles'
  // Employee Management
  | 'employees.view'
  | 'employees.create'
  | 'employees.edit'
  | 'employees.delete'
  | 'employees.approve'
  // Partner Management
  | 'partners.view'
  | 'partners.create'
  | 'partners.edit'
  | 'partners.delete'
  | 'partners.verify'
  // Inspection Management
  | 'inspections.view'
  | 'inspections.create'
  | 'inspections.edit'
  | 'inspections.delete'
  | 'inspections.approve'
  // Vehicle Management
  | 'vehicles.view'
  | 'vehicles.create'
  | 'vehicles.edit'
  | 'vehicles.delete'
  // Claims Management
  | 'claims.view'
  | 'claims.create'
  | 'claims.edit'
  | 'claims.delete'
  | 'claims.approve'
  // Salvage Management
  | 'salvage.view'
  | 'salvage.create'
  | 'salvage.edit'
  | 'salvage.delete'
  // Tenant Management (Super Admin only)
  | 'tenants.view'
  | 'tenants.create'
  | 'tenants.edit'
  | 'tenants.delete'
  // Reports & Analytics
  | 'reports.view'
  | 'reports.export'
  | 'analytics.view'
  // Settings & Configuration
  | 'settings.view'
  | 'settings.edit'
  // Audit Logs
  | 'audit.view'
  | 'audit.export';

export const PERMISSIONS: Record<Permission, { label: string; description: string; category: string }> = {
  // User Management
  'users.view': { label: 'View Users', description: 'View user list and details', category: 'User Management' },
  'users.create': { label: 'Create Users', description: 'Create new user accounts', category: 'User Management' },
  'users.edit': { label: 'Edit Users', description: 'Edit user information', category: 'User Management' },
  'users.delete': { label: 'Delete Users', description: 'Delete user accounts', category: 'User Management' },
  'users.assign_roles': { label: 'Assign Roles', description: 'Assign and revoke user roles', category: 'User Management' },

  // Employee Management
  'employees.view': { label: 'View Employees', description: 'View employee list and details', category: 'Employee Management' },
  'employees.create': { label: 'Create Employees', description: 'Onboard new employees', category: 'Employee Management' },
  'employees.edit': { label: 'Edit Employees', description: 'Edit employee information', category: 'Employee Management' },
  'employees.delete': { label: 'Delete Employees', description: 'Delete employee records', category: 'Employee Management' },
  'employees.approve': { label: 'Approve Employees', description: 'Approve employee onboarding', category: 'Employee Management' },

  // Partner Management
  'partners.view': { label: 'View Partners', description: 'View partner list and details', category: 'Partner Management' },
  'partners.create': { label: 'Create Partners', description: 'Onboard new partners', category: 'Partner Management' },
  'partners.edit': { label: 'Edit Partners', description: 'Edit partner information', category: 'Partner Management' },
  'partners.delete': { label: 'Delete Partners', description: 'Delete partner records', category: 'Partner Management' },
  'partners.verify': { label: 'Verify Partners', description: 'Verify partner documents', category: 'Partner Management' },

  // Inspection Management
  'inspections.view': { label: 'View Inspections', description: 'View inspection list and details', category: 'Inspection Management' },
  'inspections.create': { label: 'Create Inspections', description: 'Create new inspections', category: 'Inspection Management' },
  'inspections.edit': { label: 'Edit Inspections', description: 'Edit inspection details', category: 'Inspection Management' },
  'inspections.delete': { label: 'Delete Inspections', description: 'Delete inspection records', category: 'Inspection Management' },
  'inspections.approve': { label: 'Approve Inspections', description: 'Approve inspection reports', category: 'Inspection Management' },

  // Vehicle Management
  'vehicles.view': { label: 'View Vehicles', description: 'View vehicle list and details', category: 'Vehicle Management' },
  'vehicles.create': { label: 'Create Vehicles', description: 'Register new vehicles', category: 'Vehicle Management' },
  'vehicles.edit': { label: 'Edit Vehicles', description: 'Edit vehicle information', category: 'Vehicle Management' },
  'vehicles.delete': { label: 'Delete Vehicles', description: 'Delete vehicle records', category: 'Vehicle Management' },

  // Claims Management
  'claims.view': { label: 'View Claims', description: 'View claims list and details', category: 'Claims Management' },
  'claims.create': { label: 'Create Claims', description: 'Create new claims', category: 'Claims Management' },
  'claims.edit': { label: 'Edit Claims', description: 'Edit claim information', category: 'Claims Management' },
  'claims.delete': { label: 'Delete Claims', description: 'Delete claim records', category: 'Claims Management' },
  'claims.approve': { label: 'Approve Claims', description: 'Approve claims for payment', category: 'Claims Management' },

  // Salvage Management
  'salvage.view': { label: 'View Salvage', description: 'View salvage items', category: 'Salvage Management' },
  'salvage.create': { label: 'Create Salvage', description: 'Create salvage records', category: 'Salvage Management' },
  'salvage.edit': { label: 'Edit Salvage', description: 'Edit salvage information', category: 'Salvage Management' },
  'salvage.delete': { label: 'Delete Salvage', description: 'Delete salvage records', category: 'Salvage Management' },

  // Tenant Management
  'tenants.view': { label: 'View Tenants', description: 'View tenant list and details', category: 'Tenant Management' },
  'tenants.create': { label: 'Create Tenants', description: 'Create new tenants', category: 'Tenant Management' },
  'tenants.edit': { label: 'Edit Tenants', description: 'Edit tenant information', category: 'Tenant Management' },
  'tenants.delete': { label: 'Delete Tenants', description: 'Delete tenant accounts', category: 'Tenant Management' },

  // Reports & Analytics
  'reports.view': { label: 'View Reports', description: 'View system reports', category: 'Reports & Analytics' },
  'reports.export': { label: 'Export Reports', description: 'Export reports to CSV/PDF', category: 'Reports & Analytics' },
  'analytics.view': { label: 'View Analytics', description: 'View analytics dashboards', category: 'Reports & Analytics' },

  // Settings & Configuration
  'settings.view': { label: 'View Settings', description: 'View system settings', category: 'Settings' },
  'settings.edit': { label: 'Edit Settings', description: 'Modify system settings', category: 'Settings' },

  // Audit Logs
  'audit.view': { label: 'View Audit Logs', description: 'View system audit logs', category: 'Audit' },
  'audit.export': { label: 'Export Audit Logs', description: 'Export audit logs', category: 'Audit' },
};

// ========== Role-Permission Matrix ==========

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  super_admin: [
    // Full access to everything
    'users.view', 'users.create', 'users.edit', 'users.delete', 'users.assign_roles',
    'employees.view', 'employees.create', 'employees.edit', 'employees.delete', 'employees.approve',
    'partners.view', 'partners.create', 'partners.edit', 'partners.delete', 'partners.verify',
    'inspections.view', 'inspections.create', 'inspections.edit', 'inspections.delete', 'inspections.approve',
    'vehicles.view', 'vehicles.create', 'vehicles.edit', 'vehicles.delete',
    'claims.view', 'claims.create', 'claims.edit', 'claims.delete', 'claims.approve',
    'salvage.view', 'salvage.create', 'salvage.edit', 'salvage.delete',
    'tenants.view', 'tenants.create', 'tenants.edit', 'tenants.delete',
    'reports.view', 'reports.export', 'analytics.view',
    'settings.view', 'settings.edit',
    'audit.view', 'audit.export',
  ],
  platform_admin: [
    'users.view', 'users.create', 'users.edit', 'users.assign_roles',
    'employees.view', 'employees.create', 'employees.edit', 'employees.approve',
    'partners.view', 'partners.create', 'partners.edit', 'partners.verify',
    'inspections.view', 'inspections.edit', 'inspections.approve',
    'vehicles.view', 'vehicles.create', 'vehicles.edit',
    'claims.view', 'claims.edit', 'claims.approve',
    'salvage.view', 'salvage.create', 'salvage.edit',
    'reports.view', 'reports.export', 'analytics.view',
    'settings.view', 'settings.edit',
    'audit.view',
  ],
  claims_adjuster: [
    'inspections.view', 'inspections.edit',
    'vehicles.view',
    'claims.view', 'claims.create', 'claims.edit', 'claims.approve',
    'reports.view',
  ],
  verifier: [
    'inspections.view', 'inspections.approve',
    'vehicles.view',
    'partners.view',
    'reports.view',
  ],
  insurer_admin: [
    'users.view', 'users.create', 'users.edit',
    'inspections.view', 'inspections.create',
    'vehicles.view', 'vehicles.create', 'vehicles.edit',
    'claims.view', 'claims.create', 'claims.edit',
    'reports.view', 'reports.export', 'analytics.view',
    'settings.view',
  ],
  insurer_user: [
    'inspections.view', 'inspections.create',
    'vehicles.view', 'vehicles.create',
    'claims.view', 'claims.create',
    'reports.view',
  ],
  partner_admin: [
    'users.view', 'users.create', 'users.edit',
    'inspections.view', 'inspections.create', 'inspections.edit',
    'vehicles.view',
    'reports.view', 'analytics.view',
    'settings.view',
  ],
  partner_user: [
    'inspections.view', 'inspections.create',
    'vehicles.view',
    'reports.view',
  ],
  salvage_admin: [
    'users.view', 'users.create', 'users.edit',
    'salvage.view', 'salvage.create', 'salvage.edit', 'salvage.delete',
    'vehicles.view',
    'reports.view', 'analytics.view',
    'settings.view',
  ],
  salvage_user: [
    'salvage.view', 'salvage.create', 'salvage.edit',
    'vehicles.view',
    'reports.view',
  ],
  field_agent: [
    'inspections.view', 'inspections.create', 'inspections.edit',
    'vehicles.view',
    'reports.view',
  ],
};

// ========== User Interfaces ==========

export interface User {
  id: string;
  email: string;
  full_name: string;
  user_type: string;
  roles: UserRole[];
  activeRole?: UserRole;
  permissions?: Permission[];
  tenant_id?: string;
  tenant_name?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  last_login_at?: string;
}

export interface RoleAssignment {
  user_id: string;
  role: UserRole;
  assigned_by: string;
  assigned_at: string;
}

export interface PermissionGrant {
  user_id: string;
  permission: Permission;
  granted_by: string;
  granted_at: string;
}

// ========== Helper Functions ==========

export function hasPermission(user: User | null, permission: Permission): boolean {
  if (!user) return false;

  // Check explicit permissions
  if (user.permissions?.includes(permission)) return true;

  // Check role-based permissions
  const rolePermissions = user.roles?.flatMap(role => ROLE_PERMISSIONS[role] || []);
  return rolePermissions?.includes(permission) || false;
}

export function hasAnyPermission(user: User | null, permissions: Permission[]): boolean {
  return permissions.some(permission => hasPermission(user, permission));
}

export function hasAllPermissions(user: User | null, permissions: Permission[]): boolean {
  return permissions.every(permission => hasPermission(user, permission));
}

export function hasRole(user: User | null, role: UserRole): boolean {
  if (!user) return false;
  return user.roles?.includes(role) || false;
}

export function hasAnyRole(user: User | null, roles: UserRole[]): boolean {
  return roles.some(role => hasRole(user, role));
}

export function getUserPermissions(user: User | null): Permission[] {
  if (!user) return [];

  const rolePerms = user.roles?.flatMap(role => ROLE_PERMISSIONS[role] || []) || [];
  const explicitPerms = user.permissions || [];

  // Combine and deduplicate
  return Array.from(new Set([...rolePerms, ...explicitPerms]));
}

export function getPermissionsByCategory(): Record<string, Permission[]> {
  const categories: Record<string, Permission[]> = {};

  Object.entries(PERMISSIONS).forEach(([permission, meta]) => {
    if (!categories[meta.category]) {
      categories[meta.category] = [];
    }
    categories[meta.category]!.push(permission as Permission);
  });

  return categories;
}
